/** @prettier */
import { List, Map, OrderedMap, Record } from 'immutable';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';

import GlobalBannerPortal from '@xanadu/ui-shared/components/GlobalBannerPortal';
import PreCannedBetBuilders from '@xanadu/ui-shared/components/PreCannedBetBuilders';
import SEOBlurb from '@xanadu/ui-shared/components/SEOBlurb';
import EventTime from '@xanadu/ui-shared/components/event/EventTime';
import CustomSelect from '@xanadu/ui-shared/components/widgets/Select';
import Tabs from '@xanadu/ui-shared/components/widgets/Tabs';
import { BET_SOURCES } from '@xanadu/ui-shared/constants/unsubmittedOffers';
import useBettingOpeningTime from '@xanadu/ui-shared/hooks/useBettingOpeningTime';
import useInterval from '@xanadu/ui-shared/hooks/useInterval';
import useMediaQuery from '@xanadu/ui-shared/hooks/useMediaQuery';
import { track } from '@xanadu/ui-shared/interfaces/woopra';

import {
  DEFAULT_NAVIGATION_TAG,
  OUTRIGHT_EVENT_TYPES,
  PRODUCT_ROUTES,
  RACING_SPORTS,
} from '../../../constants';

import BetBuilderWidget from '../../../containers/BetBuilderWidget';
import Currency from '../../../containers/Currency';
import { DataHook } from '../../../containers/DataHook';
import SiblingsEventsCustomDropdown from '../../../containers/SiblingsEventsCustomDropdown';
import Streaming from '../../../containers/Streaming';
import Breadcrumbs from '../../../containers/event/Breadcrumbs';
import Market from '../../../containers/event/Market';
import EventsTimes from '../../../containers/events/EventsTimes';

import useEventPageMetaDescription from '../hooks/useEventPageMetaDescription';
import useSiblings from '../hooks/useSiblings';
import useVisibleMarkets from '../hooks/useVisibleMarkets';

import { closedMessage, mergeMarketRunners } from '../utils';

import IconClose from '../../../../images/icons/misc/icon-close.svg';
import IconGoto from '../../../../images/icons/white/icon-goto.svg';

import StreamingClose from '../icons/close.svg';
import StreamingLock from '../icons/lock.svg';
import MessageIcon from '../icons/messageIcon.svg';
import StreamingIcon from '../icons/streaming-white.svg';

import StreamingModal from '../../SingleEventPage/StreamingModal';
import Filters from '../../event/Filters';
import EventName from '../EventName';
import sharedStyles from '../shared.module.scss';

import styles from './DesktopLayout.module.scss';

const mediaQuery = window.matchMedia('(min-width: 788px) and (max-width: 1199px)');
const mediaQueryBetslip = window.matchMedia('(min-width: 1200px) and (max-width: 1643px)');

const vs = (str) => <span className={styles.vs}> {str} </span>;

export default function DesktopLayout({
  id,
  event,
  events,
  markets,
  isAntePost,
  eventCompetition,
  positions,
  location: { pathname, hash: locationHash },
  streamAvailable,
  streamEnd,
  streamDetails,
  isLoggedIn,
  getStream,
  matchedAmount,
  sidebarVisible,
  updateRightSidebar,
  isEventAvailableForStreaming,
  isUSARace,
  tags,
  product,
  history,
  getCalendar,
  bettingOpeningTime,
  isMBZero,
  clock,
  betbuilderEnabled,
  precannedBetBuilderData,
  widthSize,
}) {
  const hash = locationHash ? locationHash.slice(1) : '';

  const [trigger, setTrigger] = useState(false);
  const isSmallerScreen = useMediaQuery(mediaQuery);
  const isBetslipCoveredScreen = useMediaQuery(mediaQueryBetslip);
  const [selectedTab, setTab] = useState(hash);
  const [streamFinished, setStreamFinished] = useState();
  const [mobileStream, toggleMobileStream] = useState(false);
  const [sortType, setSortType] = useState('odds');
  const [openStreamingModal, setOpenStreamingModal] = useState(false);
  const [runnerFilterValue, setRunnerFilterValue] = useState('');
  const [showRacingInfo, setShowRacingInfo] = useState(false);

  const productUrlPrefix = PRODUCT_ROUTES[product];

  const sport = event.metaTags.find((t) => t.type === 'SPORT') || DEFAULT_NAVIGATION_TAG;

  const countryTag = event.metaTags.find((t) => t.type === 'COUNTRY') || DEFAULT_NAVIGATION_TAG;

  const sportUrlName = sport.urlName;

  const isRacing = RACING_SPORTS.includes(sportUrlName);

  const isHorseRace = sportUrlName === 'horse-racing';

  let groupedEvents = [];
  let availableCalendar = [];

  const { showBettingOpeningTime, bettingOpeningTimeText } = useBettingOpeningTime({
    isMBZero,
    isRacing,
    sportUrlName,
    eventStartTime: event.start,
    bettingOpeningTime,
    clock,
  });

  if (isRacing) {
    const siblings = useSiblings({ tags, countryTag, sportUrlName });
    const calendarId = `${product}_${sportUrlName}`;

    const calendar = events && events.get(calendarId);

    availableCalendar = calendar ? calendar.first() : null;

    groupedEvents = siblings.filter((sibling) => {
      let totalData = false;
      if (availableCalendar) {
        totalData = availableCalendar.events.find((evnt) => {
          const locationTag = evnt.metaTags.find((tag) => tag.type === 'LOCATION') || {};
          return locationTag.id === sibling.id;
        });
      }
      return totalData;
    });
  }

  const onFilterChange = ({ value }) => {
    setRunnerFilterValue(value);
  };

  const onToggleRacingInfo = () => {
    setShowRacingInfo(!showRacingInfo);
  };

  const clearFilter = () => {
    setTimeout(() => {
      setRunnerFilterValue('');
    }, 0);
  };

  const onRunnersSortChange = useCallback((sortTypeUpdate) => {
    setSortType(sortTypeUpdate);
  }, []);

  const onSortRunners = ({ value }) => {
    onRunnersSortChange(value);
  };

  useInterval(() => {
    setTrigger(!trigger);
  }, 30000);

  useEffect(() => {
    if (isRacing) {
      getCalendar({ tags: [sport.urlName] });
    }
  }, [isRacing]);

  useEffect(() => {
    if (!mobileStream && isSmallerScreen && isEventAvailableForStreaming && id) {
      getStream({ event: id, isNextStreamRequest: false });
    }
  }, [matchedAmount, id, isLoggedIn, isEventAvailableForStreaming]);

  useEffect(() => {
    if (streamEnd !== null) {
      const currentDate = moment();
      const endTime = moment(streamEnd).utc();
      setStreamFinished(currentDate.isAfter(endTime));
    }
  }, [streamEnd, trigger]);

  useEffect(() => {
    if (streamFinished) {
      toggleMobileStream(false);
    }
  }, [streamFinished]);

  useEffect(() => {
    setRunnerFilterValue('');
    setTab();
  }, [id]);

  useEffect(() => {
    setTab(hash);
  }, [hash]);

  const onSetTab = useCallback(
    (tab) => {
      setTab(tab);

      if (tab === BET_SOURCES.betBuilder) {
        track('Marketing', 'Click on Bet Builder tab');
      }
    },
    [setTab, track]
  );

  const isClosed = event.status === 'closed' || event.status === 'unavailable';

  const pageTitle = isMBZero
    ? `${event.name} Best Odds | Zero % Margin Betting | Matchbook Zero`
    : `${event.name} Exchange Betting | ${event.name} Back & Lay Betting | Matchbook Betting Exchange.`;

  const isOutright = event.metaTags.some(({ urlName }) => OUTRIGHT_EVENT_TYPES.includes(urlName));

  const tagsSegment = pathname.split('/events/');
  const splat = tagsSegment[1] || '';

  // remove duplicate slashes and turn into array of path segments and drop empty segments
  const segments = splat
    .replace(/(\/)+/g, '$1')
    .split('/')
    .filter((x) => !!x);

  // NOTE: Wait for react helmet v6 before we can do this the recommended way
  // https://github.com/nfl/react-helmet/issues/373
  const pageMeta = [
    {
      name: 'apple-itunes-app',
      content: 'app-id=503101282',
      'app-argument': `https://www.matchbook.com${pathname}`,
    },
  ];

  if (isClosed) {
    pageMeta.push(
      { name: 'prerender-status-code', content: '404' },
      { name: 'prerender-header', content: '' }
    );
  }

  const raceInfo = [
    ...(event.raceInfo.raceLength
      ? [event.raceInfo.raceType, event.raceInfo.raceClass, event.raceInfo.raceLength]
      : []),
  ]
    .filter((item) => item)
    .join(' | ');

  const crossSellingLink = pathname.replace('zero/', '');

  const { tabs, defaultTab, visibleMarkets, useAdvancedMarketView } = useVisibleMarkets({
    markets,
    positions,
    sportUrlName,
    selectedTab,
    isOutright,
    showClosedMarkets: isMBZero,
    locationPathname: pathname,
    betbuilderEnabled,
    isMBZero,
  });

  const groupedMarketsByLines = visibleMarkets.sortBy((m) => m.name).groupBy((m) => m.name);

  const computedVisibleMarkets = ['handicap', 'total'].includes(selectedTab)
    ? mergeMarketRunners(groupedMarketsByLines)
    : visibleMarkets;

  const pageMetaDescription = useEventPageMetaDescription({
    sportUrlName,
    eventName: event.name,
    eventStartTime: event.start,
    eventCompetition: eventCompetition && eventCompetition.name,
    isAntePost,
    isMBZero,
  });

  const canonicalUrl = window.location.href.replace(`${PRODUCT_ROUTES[product]}/`, '/');

  const onCategoryShortcutClick = (option, urlRedirection) => {
    if (isRacing) {
      const racingEvent =
        availableCalendar.events.find((envt) =>
          envt.metaTags.find((meta) => meta.urlName === option.urlName)
        ) || {};
      history.push(`${productUrlPrefix}/events/${racingEvent.id}`);
    } else {
      history.push(`${productUrlPrefix}/events${urlRedirection}`);
    }
  };

  // We show the filters only if there is one visible market and the sport is one of HR, GRH and Golf
  const showFilters = visibleMarkets.size === 1 && useAdvancedMarketView;

  const handleStreamingClose = () => {
    setOpenStreamingModal(false);
  };

  const showBetBuilderTab = defaultTab === BET_SOURCES.betBuilder && betbuilderEnabled && !isMBZero;

  const showPrecannedBetBuilder =
    !isMBZero && betbuilderEnabled && [defaultTab, selectedTab].includes('popular');

  useEffect(() => {
    if (hash && !computedVisibleMarkets.size && !showBetBuilderTab) {
      setTab(tabs[0].value);
    }
  }, [hash, computedVisibleMarkets, showBetBuilderTab]);

  let streaming = (
    <span
      aria-label={`Live Stream ${streamAvailable ? 'A' : 'Una'}vailable`}
      className={`hint hint--top ${styles.icon} ${!streamAvailable && styles.disabled}`}
    >
      <StreamingIcon className={styles.iconSVG} />
    </span>
  );

  if (isBetslipCoveredScreen) {
    streaming = (
      <span
        role="button"
        aria-label={`Live Stream ${streamAvailable ? 'A' : 'Una'}vailable`}
        className={`hint hint--top ${styles.icon} ${!streamAvailable && styles.disabled}`}
        onClick={() => {
          if (streamAvailable && sidebarVisible) {
            updateRightSidebar({
              content: null,
              visible: false,
            });
          }
        }}
      >
        <StreamingIcon className={styles.iconSVG} />
      </span>
    );
  }

  if (
    isSmallerScreen &&
    streamAvailable &&
    (!isLoggedIn || (streamDetails && streamDetails.error !== null))
  ) {
    streaming = (
      <span
        role="button"
        aria-label={`Live Stream ${streamAvailable ? 'A' : 'Una'}vailable`}
        className={`hint hint--top
          ${styles.icon}
          ${streamAvailable && styles.streamIcon}
          ${!streamAvailable && styles.disabled}`}
        onClick={() => {
          setOpenStreamingModal(true);
        }}
      >
        <span className={styles.iconText}>Watch Now</span>
        <StreamingLock />
      </span>
    );
  } else if (isSmallerScreen && streamAvailable && isLoggedIn && streamDetails !== undefined) {
    streaming = (
      <span
        role="button"
        aria-label={`Live Stream ${streamAvailable ? 'A' : 'Una'}vailable`}
        className={`hint hint--top
          ${styles.icon}
          ${streamAvailable && styles.streamIcon}
          ${!streamAvailable && styles.disabled}`}
        onClick={() => {
          if (streamAvailable) {
            toggleMobileStream(true);
          }
        }}
      >
        <span className={styles.iconText}>Watch Now</span>
        <StreamingIcon />
      </span>
    );
  }

  if (streamFinished || sportUrlName === 'soccer') {
    streaming = null;
  }

  const computedSeoBlurbPathUrl = useMemo(() => {
    const lastSegment = segments[segments.length - 1];
    return `/${lastSegment}`;
  }, [segments]);

  return (
    <div className={styles.SingleEventPage}>
      <Helmet meta={pageMeta}>
        <title>{pageTitle}</title>
        {isMBZero && <link rel="canonical" href={canonicalUrl} />}
        {pageMetaDescription && <meta name="description" content={pageMetaDescription} />}
      </Helmet>
      <GlobalBannerPortal />
      <main className={styles.main}>
        <header className={sharedStyles.header}>
          {!mobileStream && (
            <div className={styles.topRow}>
              <div className={styles.breadcrumbWrapper}>
                <Breadcrumbs id={id} segments={segments.slice(0, 3)} noSelect />
              </div>
              {isMBZero && (
                <Link
                  to={crossSellingLink}
                  className={styles.mbzeroLink}
                  onClick={() => {
                    track('MBZero', 'CrossSelling Click', { description: crossSellingLink });
                  }}
                >
                  <IconGoto />
                  View this page on Matchbook Exchange
                </Link>
              )}
            </div>
          )}
          {openStreamingModal && (
            <StreamingModal
              isOpen={openStreamingModal}
              isLoggedIn={isLoggedIn}
              isHorseRace={isHorseRace}
              streamDetails={streamDetails}
              handleModalClose={handleStreamingClose}
            />
          )}
          {!mobileStream && (
            <div className={styles.middleRow}>
              {!isRacing && (
                <EventTime
                  start={event.start}
                  scoresPhase={event.scores.phase}
                  scoresHome={event.scores.home}
                  scoresAway={event.scores.away}
                  scoresType={event.scores.type}
                  scoresElapsedTimeMinutes={event.scores.elapsedTimeMinutes}
                  inRunningFlag={event.inRunningFlag}
                  status={event.status}
                />
              )}
              <div className={styles.info}>
                <h1 className={styles.eventName}>
                  <EventName vs={vs}>{event.name}</EventName>
                  {!isAntePost && (
                    <CustomSelect
                      optionsList={groupedEvents}
                      onSelectOption={onCategoryShortcutClick}
                      widthSize={widthSize}
                    />
                  )}
                  {(!isRacing || isAntePost) && <SiblingsEventsCustomDropdown eventId={event.id} />}
                </h1>
                <span className={styles.matchedVolume}>{raceInfo}</span>
                <span className={styles.matchedVolume}>
                  {!isMBZero && (
                    <Fragment>
                      {' Matched Volume: '}
                      <Currency precision={0} value={event.volume} />
                    </Fragment>
                  )}
                </span>
              </div>
            </div>
          )}
          {isRacing && !mobileStream && <EventsTimes sportType={sport.urlName} event={event} />}
          {mobileStream ? (
            <div className={sharedStyles.streamContainer}>
              <div className={sharedStyles.streamingHeader}>
                <span className={sharedStyles.streamingEventHeader}>
                  <EventName vs={vs}>{event.name}</EventName>
                </span>
                <span
                  role="button"
                  className={sharedStyles.streamingCloseIcon}
                  onClick={() => {
                    toggleMobileStream(false);
                  }}
                >
                  <StreamingClose />
                </span>
              </div>
              <Streaming
                isEventAvailableForStreaming={isEventAvailableForStreaming}
                isUSARace={isUSARace}
                sportUrlName={sportUrlName}
                pip={false}
                mobileStream
              />
            </div>
          ) : null}
          {(showFilters || (isEventAvailableForStreaming && sportUrlName !== 'soccer')) && (
            <div className={styles.filtersContainer}>
              {showFilters ? (
                <Filters
                  sortType={sortType}
                  filterValue={runnerFilterValue}
                  sportUrlName={sport.urlName}
                  onFilterChange={onFilterChange}
                  onSortChange={onSortRunners}
                  onClearFilter={clearFilter}
                />
              ) : (
                <div />
              )}
              <div className={styles.rightContent}>
                <div className={styles.icons}>
                  {isEventAvailableForStreaming ? streaming : null}
                </div>
                {isRacing && !isAntePost && (
                  <div
                    className={sharedStyles.infoIcon}
                    onClick={(e) => onToggleRacingInfo(e)}
                    role="button"
                    aria-label={'Race Info'}
                    tabIndex={0}
                  >
                    {showRacingInfo ? <IconClose /> : <MessageIcon />}
                  </div>
                )}
              </div>
            </div>
          )}
          {tabs.length > 1 && (
            <Tabs onSelect={onSetTab} value={defaultTab} theme="dark" tabs={tabs} collapse />
          )}
        </header>
        {isClosed && !isMBZero && !visibleMarkets.size && (
          <div className={sharedStyles.closedStatus}>{closedMessage}</div>
        )}
        <div className={sharedStyles.markets}>
          {showBetBuilderTab ? (
            <BetBuilderWidget eventId={id} loading={selectedTab === BET_SOURCES.betBuilder} />
          ) : (
            <Fragment>
              {showPrecannedBetBuilder && (
                <PreCannedBetBuilders
                  data={precannedBetBuilderData}
                  eventId={id}
                  isSingleEventPage
                />
              )}
              {computedVisibleMarkets
                .map((market, index) => (
                  <DataHook append={`market-${index}`} key={market.id}>
                    <Market
                      isMBZero={isMBZero}
                      priceDepth={3}
                      display={'compact'}
                      staySubscribed
                      sport={sport}
                      id={market.id}
                      listPosition={index}
                      sortType={sortType}
                      selectedTab={selectedTab}
                      combinedRunners={market.runners || []}
                      handicaps={market.handicaps}
                      mainLine={market.mainLine}
                      groupedMarketsByLines={groupedMarketsByLines}
                      showBettingOpeningTime={index === 0 && showBettingOpeningTime}
                      bettingOpeningTimeText={bettingOpeningTimeText}
                      runnerFilterValue={runnerFilterValue}
                      setRunnerFilterValue={setRunnerFilterValue}
                      showFilters={showFilters}
                      isRacing={isRacing}
                      showRacingInfo={showRacingInfo}
                    />
                  </DataHook>
                ))
                .toArray()}
            </Fragment>
          )}
        </div>
      </main>
      <SEOBlurb pathUrl={computedSeoBlurbPathUrl} />
    </div>
  );
}

DesktopLayout.propTypes = {
  event: PropTypes.instanceOf(Record),
  events: PropTypes.instanceOf(Map),
  id: PropTypes.string.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    hash: PropTypes.string.isRequired,
  }).isRequired,
  betbuilderEnabled: PropTypes.bool,
  markets: PropTypes.instanceOf(OrderedMap),
  positions: PropTypes.instanceOf(Map),
  precannedBetBuilderData: PropTypes.instanceOf(List),
  streamAvailable: PropTypes.string,
  streamEnd: PropTypes.string,
  streamDetails: PropTypes.object,
  isLoggedIn: PropTypes.bool.isRequired,
  sidebarVisible: PropTypes.bool.isRequired,
  matchedAmount: PropTypes.number,
  getCalendar: PropTypes.func.isRequired,
  getStream: PropTypes.func.isRequired,
  updateRightSidebar: PropTypes.func.isRequired,
  isAntePost: PropTypes.bool.isRequired,
  eventCompetition: PropTypes.instanceOf(Record).isRequired,
  isEventAvailableForStreaming: PropTypes.bool.isRequired,
  isUSARace: PropTypes.bool.isRequired,
  isMBZero: PropTypes.bool.isRequired,
  product: PropTypes.string.isRequired,
  clock: PropTypes.number,
  bettingOpeningTime: PropTypes.string.isRequired,
  tags: PropTypes.object,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  widthSize: PropTypes.number,
};
